import { create } from 'zustand';

// ==============================
// Type Definitions
// ==============================

export type OnboardingData = {
  firmDetails: {
    painPoints: number[]; // Array of pain point IDs
  };
  createAccountAndContact: {
    createdAccountUuid?: string;
    accountNumericId?: number;
    createdAccountName?: string;
    accountStepSkipped?: boolean;
    selectedContactId?: string;
    contactNumericId?: number;
    selectedContactPrimaryEmail?: string;
    selectedContactDisplayName?: string;
    selectedContactIsExisting?: boolean;
    selectedContactSource?: 'existing_contact' | 'third_party_contact';
    selectedAccountUuid?: string;
    selectedAccountName?: string;
    selectedAccountIsExisting?: boolean;
  };
  messaging: {
    hasGeneratedReply: boolean;
    hasSentMessage: boolean;
  };
  fileIntelligence: {
    hasUploadedFile: boolean;
    uploadedFileName?: string;
    uploadedFileId?: number;
    aiIngestionStatus?: 'pending' | 'completed' | 'failed';
  };
};

export type OnboardingState = {
  data: OnboardingData;
  isLoading: boolean;
  isCreatingAccount: boolean;
  submitCreateAccountHandler?: (() => void) | null;
  isCreatingContact: boolean;
  submitCreateContactHandler?: (() => void) | null;
  error: string | null;
};

export type OnboardingActions = {
  // Firm Details Actions
  setPainPoints: (painPoints: number[]) => void;

  // Messaging Actions
  setHasGeneratedReply: (value: boolean) => void;
  setHasSentMessage: (value: boolean) => void;

  // File Intelligence Actions
  setHasUploadedFile: (
    value: boolean,
    fileName?: string,
    fileId?: number
  ) => void;
  setAiIngestionStatus: (status: 'pending' | 'completed' | 'failed') => void;

  // Create account (step 2) actions
  setCreatedAccountUuid: (uuid: string, numericId?: number) => void;
  setCreatedAccountName: (name: string) => void;
  setAccountStepSkipped: (skipped: boolean) => void;
  skipToContactStep: () => void;
  clearAccountStepSkipped: () => void;
  setIsCreatingAccount: (value: boolean) => void;
  setSubmitCreateAccountHandler: (handler: (() => void) | null) => void;
  setIsCreatingContact: (value: boolean) => void;
  setSubmitCreateContactHandler: (handler: (() => void) | null) => void;
  setSelectedContactSelection?: (selection: {
    id?: string;
    numericId?: number;
    primaryEmail: string;
    displayName?: string;
    isExisting: boolean;
    source: 'existing_contact' | 'third_party_contact';
  }) => void;
  clearSelectedContactSelection?: () => void;
  setSelectedAccountSelection?: (selection: {
    uuid: string;
    numericId?: number;
    name: string;
    isExisting: boolean;
  }) => void;
  clearSelectedAccountSelection?: () => void;

  // General Actions
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  resetOnboarding: () => void;
};

// ==============================
// Initial State
// ==============================

const initialState: OnboardingState = {
  data: {
    firmDetails: {
      painPoints: [],
    },
    createAccountAndContact: {},
    messaging: {
      hasGeneratedReply: false,
      hasSentMessage: false,
    },
    fileIntelligence: {
      hasUploadedFile: false,
      uploadedFileName: undefined,
    },
  },
  isLoading: false,
  isCreatingAccount: false,
  submitCreateAccountHandler: null,
  isCreatingContact: false,
  submitCreateContactHandler: null,
  error: null,
};

// ==============================
// Store Definition
// ==============================

export const useOnboardingStore = create<OnboardingState & OnboardingActions>(
  (set) => ({
    ...initialState,

    // Firm Details Actions
    setPainPoints: (painPoints: number[]) =>
      set((state) => ({
        data: {
          ...state.data,
          firmDetails: {
            ...state.data.firmDetails,
            painPoints,
          },
        },
      })),

    // Messaging Actions
    setHasGeneratedReply: (value: boolean) =>
      set((state) => ({
        data: {
          ...state.data,
          messaging: {
            ...state.data.messaging,
            hasGeneratedReply: value,
          },
        },
      })),

    setHasSentMessage: (value: boolean) =>
      set((state) => ({
        data: {
          ...state.data,
          messaging: {
            ...state.data.messaging,
            hasSentMessage: value,
          },
        },
      })),

    // File Intelligence Actions
    setHasUploadedFile: (value: boolean, fileName?: string, fileId?: number) =>
      set((state) => ({
        data: {
          ...state.data,
          fileIntelligence: {
            ...state.data.fileIntelligence,
            hasUploadedFile: value,
            uploadedFileName: fileName,
            uploadedFileId: fileId,
          },
        },
      })),

    setAiIngestionStatus: (status: 'pending' | 'completed' | 'failed') =>
      set((state) => ({
        data: {
          ...state.data,
          fileIntelligence: {
            ...state.data.fileIntelligence,
            aiIngestionStatus: status,
          },
        },
      })),

    setCreatedAccountUuid: (uuid: string, numericId?: number) =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            createdAccountUuid: uuid,
            accountNumericId: numericId,
            accountStepSkipped: false,
          },
        },
      })),

    setCreatedAccountName: (name: string) =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            createdAccountName: name,
          },
        },
      })),

    setAccountStepSkipped: (skipped: boolean) =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            accountStepSkipped: skipped,
          },
        },
      })),

    skipToContactStep: () =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            accountStepSkipped: true,
          },
        },
      })),

    clearAccountStepSkipped: () =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            accountStepSkipped: false,
          },
        },
      })),
    setSelectedContactSelection: (selection) =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            selectedContactId: selection.id,
            contactNumericId: selection.numericId,
            selectedContactPrimaryEmail: selection.primaryEmail,
            selectedContactDisplayName: selection.displayName,
            selectedContactIsExisting: selection.isExisting,
            selectedContactSource: selection.source,
          },
        },
      })),
    clearSelectedContactSelection: () =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            selectedContactId: undefined,
            contactNumericId: undefined,
            selectedContactPrimaryEmail: undefined,
            selectedContactDisplayName: undefined,
            selectedContactIsExisting: undefined,
            selectedContactSource: undefined,
          },
        },
      })),

    setSelectedAccountSelection: (selection) =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            selectedAccountUuid: selection.uuid,
            accountNumericId: selection.numericId,
            selectedAccountName: selection.name,
            selectedAccountIsExisting: selection.isExisting,
          },
        },
      })),
    clearSelectedAccountSelection: () =>
      set((state) => ({
        data: {
          ...state.data,
          createAccountAndContact: {
            ...state.data.createAccountAndContact,
            selectedAccountUuid: undefined,
            accountNumericId: undefined,
            selectedAccountName: undefined,
            selectedAccountIsExisting: undefined,
          },
        },
      })),

    setIsCreatingAccount: (value: boolean) => set({ isCreatingAccount: value }),
    setSubmitCreateAccountHandler: (handler: (() => void) | null) =>
      set({ submitCreateAccountHandler: handler }),
    setIsCreatingContact: (value: boolean) => set({ isCreatingContact: value }),
    setSubmitCreateContactHandler: (handler: (() => void) | null) =>
      set({ submitCreateContactHandler: handler }),

    // General Actions
    setLoading: (isLoading: boolean) => set({ isLoading }),
    setError: (error: string | null) => set({ error }),
    resetOnboarding: () => set(initialState),
  })
);

// ==============================
// Selectors
// ==============================

export const useOnboardingData = () =>
  useOnboardingStore((state) => state.data);

export const useFirmDetailsPainPoints = () =>
  useOnboardingStore((state) => state.data.firmDetails.painPoints);

export const useMessagingData = () =>
  useOnboardingStore((state) => state.data.messaging);

export const useFileIntelligenceData = () =>
  useOnboardingStore((state) => state.data.fileIntelligence);

export const useOnboardingLoading = () =>
  useOnboardingStore((state) => state.isLoading);

export const useOnboardingError = () =>
  useOnboardingStore((state) => state.error);

export const useUploadedFileId = () =>
  useOnboardingStore((state) => state.data.fileIntelligence.uploadedFileId);

export const useAiIngestionStatus = () =>
  useOnboardingStore((state) => state.data.fileIntelligence.aiIngestionStatus);

export const useCreatedAccountUuid = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.createdAccountUuid
  );

export const useCreatedAccountName = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.createdAccountName
  );

export const useAccountStepSkipped = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.accountStepSkipped
  );

export const useIsCreatingAccount = () =>
  useOnboardingStore((state) => state.isCreatingAccount);

export const useSubmitCreateAccountHandler = () =>
  useOnboardingStore((state) => state.submitCreateAccountHandler);

export const useIsCreatingContact = () =>
  useOnboardingStore((state) => state.isCreatingContact);

export const useSubmitCreateContactHandler = () =>
  useOnboardingStore((state) => state.submitCreateContactHandler);

export const useSelectedContactId = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedContactId
  );

export const useContactNumericId = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.contactNumericId
  );

export const useAccountNumericId = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.accountNumericId
  );

export const useSelectedContactPrimaryEmail = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedContactPrimaryEmail
  );

export const useSelectedContactDisplayName = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedContactDisplayName
  );

export const useSelectedContactIsExisting = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedContactIsExisting
  );

export const useSelectedContactSource = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedContactSource
  );

export const useSelectedAccountIsExisting = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedAccountIsExisting
  );

export const useSelectedAccountUuid = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedAccountUuid
  );

export const useSelectedAccountName = () =>
  useOnboardingStore(
    (state) => state.data.createAccountAndContact.selectedAccountName
  );

export const useOnboardingActions = () =>
  useOnboardingStore((state) => ({
    setPainPoints: state.setPainPoints,
    setHasGeneratedReply: state.setHasGeneratedReply,
    setHasSentMessage: state.setHasSentMessage,
    setHasUploadedFile: state.setHasUploadedFile,
    setAiIngestionStatus: state.setAiIngestionStatus,
    setCreatedAccountUuid: state.setCreatedAccountUuid,
    setCreatedAccountName: state.setCreatedAccountName,
    setAccountStepSkipped: state.setAccountStepSkipped,
    skipToContactStep: state.skipToContactStep,
    clearAccountStepSkipped: state.clearAccountStepSkipped,
    setIsCreatingAccount: state.setIsCreatingAccount,
    setSubmitCreateAccountHandler: state.setSubmitCreateAccountHandler,
    setIsCreatingContact: state.setIsCreatingContact,
    setSubmitCreateContactHandler: state.setSubmitCreateContactHandler,
    setSelectedContactSelection: state.setSelectedContactSelection,
    clearSelectedContactSelection: state.clearSelectedContactSelection,
    setSelectedAccountSelection: state.setSelectedAccountSelection,
    clearSelectedAccountSelection: state.clearSelectedAccountSelection,
    setLoading: state.setLoading,
    setError: state.setError,
    resetOnboarding: state.resetOnboarding,
  }));
